/**
 * Marking from the browser, through the local laser agent.
 *
 * The cloud page cannot talk to the laser; the agent (OraclaseWebGui) running
 * on the operator's own machine can. This panel drives it and reports progress
 * back.
 *
 * It renders nothing at all when no agent answers, so a workstation without
 * the laser software never shows a button that cannot work.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import StopIcon from "@mui/icons-material/Stop";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import MarkingTimeChip from "./MarkingTimeChip";
import { MarkingSectionLabel } from "./MarkingOptionsBox";
import { formatMarkingTime } from "../helper/markingTime";
import {
    getAgentBaseUrl,
    getProgress,
    probeAgent,
    startMarking,
    startPreview,
    stopMarking,
    stopPreview,
    uploadMarkingFile,
} from "../utils/laserAgent";

const STATUS_POLL_MS = 3000;
const PROGRESS_POLL_MS = 1000;

// Cloud-side panel for marking directly on the laser through the local
// Oraclase laser agent (browser bridge). Same principle as the camera
// capture panel: the panel exists only while the agent is reachable on this
// computer - while probing, and on machines without the laser software,
// NOTHING is rendered (the marking-file download stays the only path). With
// an agent the panel shows what is connected before the user can click
// "Mark on laser" or "Preview" - both are enabled only when remote marking is
// permitted on the workstation and a laser is connected.
//
// "Preview" uploads the same marking file and has the agent trace its outline
// with the guide laser until "Stop preview". It is not a marking, so it asks
// for no confirmation and writes nothing to the marking-event log.
//
// Props:
//   getMarkingBlob: async () => ({ blob, filename }) - fetches the marking
//       JSON from the cloud backend (page-specific).
//   markingContext: { explorationId, iterationNumber, patchType } -
//       recorded in the backend marking-event log.
//   predictedMarkingTime: the backend's {seconds, text} prediction (or
//       seconds) for the file the panel would mark, shown beside the status
//       chips; the agent's own prediction still appears in the confirm dialog.
//   children: the page's own remote-marking controls (heading, plate
//       picker, ...). Rendered above the panel, and only together with it,
//       so the whole remote-marking section disappears with the agent.
//   sectionTitle: when set, the panel is one section of a MarkingOptionsBox:
//       it renders this label above itself and a divider below, both only
//       together with the panel, so the box keeps a single file section
//       without an agent.
export default function LaserMarkPanel({
    getMarkingBlob,
    markingContext,
    predictedMarkingTime = null,
    sectionTitle = null,
    children,
}) {
    const [agentStatus, setAgentStatus] = useState(null); // null = still probing
    const [phase, setPhase] = useState("idle"); // idle | uploading | confirm | starting | running | stopping
    const [jobMode, setJobMode] = useState("marking"); // marking | preview - what the running job is
    const [pendingAction, setPendingAction] = useState(null); // mark | preview - which button is busy
    const [summaryPayload, setSummaryPayload] = useState(null);
    const [job, setJob] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [resultMessage, setResultMessage] = useState(null);
    const phaseRef = useRef("idle");
    const jobModeRef = useRef("marking");
    const reportedRef = useRef(false);

    const setPhaseTracked = useCallback((next) => {
        phaseRef.current = next;
        setPhase(next);
    }, []);

    const setJobModeTracked = useCallback((next) => {
        jobModeRef.current = next;
        setJobMode(next);
    }, []);

    const reportMarkingEvent = useCallback(
        async (event, jobSnapshot) => {
            if (!markingContext || !markingContext.explorationId) return;
            const payload = {
                explorationId: markingContext.explorationId,
                iterationNumber: markingContext.iterationNumber,
                patchType: markingContext.patchType,
                event,
                laserType: (agentStatus && agentStatus.laser_type) || "",
                jobSnapshot: jobSnapshot || null,
                errorMessage: (jobSnapshot && jobSnapshot.error) || "",
            };
            try {
                await axios.post("api/marking/events", payload);
            } catch (error) {
                // Audit logging must never block the marking flow; retry once.
                try {
                    await axios.post("api/marking/events", payload);
                } catch (retryError) {
                    console.error("Failed to record marking event:", retryError);
                }
            }
        },
        [markingContext, agentStatus]
    );

    const handleTerminalJob = useCallback(
        (finalJob) => {
            setJob(finalJob);
            setPhaseTracked("idle");
            if (reportedRef.current) return;
            reportedRef.current = true;
            const state = finalJob.state;
            if (jobModeRef.current === "preview") {
                if (state === "failed") {
                    setErrorMessage(`Preview failed: ${finalJob.error || finalJob.message || "unknown error"}`);
                } else if (state === "stopped" || state === "completed") {
                    setResultMessage("Preview stopped.");
                }
                return;
            }
            if (state === "completed") {
                setResultMessage("Marking completed on the laser.");
                setErrorMessage(null);
                reportMarkingEvent("COMPLETED", finalJob);
            } else if (state === "stopped") {
                setResultMessage("Marking stopped.");
                reportMarkingEvent("STOPPED", finalJob);
            } else if (state === "failed") {
                setErrorMessage(`Marking failed: ${finalJob.error || finalJob.message || "unknown error"}`);
                reportMarkingEvent("FAILED", finalJob);
            }
        },
        [reportMarkingEvent, setPhaseTracked]
    );

    // Status poll: keeps the card truthful about what is connected, and
    // re-attaches to a job that is already running on the agent (e.g. after
    // a page reload - the mark keeps running locally regardless of this tab).
    const unavailableLogged = useRef(false);
    useEffect(() => {
        let cancelled = false;
        const poll = async () => {
            const status = await probeAgent();
            if (cancelled) return;
            setAgentStatus(status);
            if (!status.available && !unavailableLogged.current) {
                // Nothing is rendered without an agent; keep the diagnosis
                // (probed URL - a per-origin localStorage.laserAgentUrl
                // override changes it - and the browser error; "Failed to
                // fetch" on a running agent usually means a CORS/origin
                // mismatch) in the console for the laser-bridge setup.
                unavailableLogged.current = true;
                console.debug(`Laser agent not reachable at ${getAgentBaseUrl()}${status.error ? ` - ${status.error}` : ""}`);
            }
            if (status.available) unavailableLogged.current = false;
            const remoteJob = status.available && status.remote ? status.remote.job : null;
            if (
                remoteJob &&
                remoteJob.state === "running" &&
                phaseRef.current !== "running" &&
                phaseRef.current !== "stopping" &&
                phaseRef.current !== "starting"
            ) {
                reportedRef.current = true; // re-attached: origin tab reports the outcome
                setJobModeTracked(remoteJob.mode === "preview" ? "preview" : "marking");
                setJob(remoteJob);
                setPhaseTracked("running");
            }
        };
        poll();
        const timer = setInterval(poll, STATUS_POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [setPhaseTracked, setJobModeTracked]);

    // Progress poll while a job is running.
    useEffect(() => {
        if (phase !== "running" && phase !== "stopping") return undefined;
        let cancelled = false;
        const poll = async () => {
            try {
                const payload = await getProgress();
                if (cancelled) return;
                const currentJob = payload.job || {};
                if (currentJob.state === "running") {
                    setJob(currentJob);
                } else {
                    handleTerminalJob(currentJob);
                }
            } catch (error) {
                if (cancelled) return;
                setErrorMessage(
                    jobModeRef.current === "preview"
                        ? "Connection to the laser agent lost - the preview may still be running on the laser."
                        : "Connection to the laser agent lost - the mark may still be running on the laser."
                );
            }
        };
        poll();
        const timer = setInterval(poll, PROGRESS_POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [phase, handleTerminalJob]);

    const handleMarkClick = async () => {
        setErrorMessage(null);
        setResultMessage(null);
        setPendingAction("mark");
        setPhaseTracked("uploading");
        try {
            const { blob, filename } = await getMarkingBlob();
            const payload = await uploadMarkingFile(blob, filename);
            setSummaryPayload({ ...payload, filename });
            setPhaseTracked("confirm");
        } catch (error) {
            setErrorMessage(`Could not send the marking file to the laser: ${error.message}`);
            setPhaseTracked("idle");
        }
    };

    const handleConfirmStart = async () => {
        setPhaseTracked("starting");
        try {
            await startMarking();
            reportedRef.current = false;
            setSummaryPayload(null);
            setJobModeTracked("marking");
            setPhaseTracked("running");
            reportMarkingEvent("STARTED", null);
        } catch (error) {
            setErrorMessage(`Could not start marking: ${error.message}`);
            setPhaseTracked("idle");
        }
    };

    const handlePreviewClick = async () => {
        setErrorMessage(null);
        setResultMessage(null);
        setPendingAction("preview");
        setPhaseTracked("uploading");
        try {
            const { blob, filename } = await getMarkingBlob();
            await uploadMarkingFile(blob, filename);
            setPhaseTracked("starting");
            await startPreview();
            reportedRef.current = false;
            setJobModeTracked("preview");
            setJob(null);
            setPhaseTracked("running");
        } catch (error) {
            setErrorMessage(`Could not start the preview: ${error.message}`);
            setPhaseTracked("idle");
        }
    };

    const handleStopClick = async () => {
        setPhaseTracked("stopping");
        try {
            if (jobModeRef.current === "preview") {
                await stopPreview();
            } else {
                await stopMarking();
            }
        } catch (error) {
            setErrorMessage(`Stop request failed: ${error.message}`);
            setPhaseTracked("running");
        }
    };

    // ---- Render helpers -------------------------------------------------

    // No agent on this machine (or still probing): the remote-marking
    // section does not exist - the page keeps its marking-file download.
    if (!agentStatus || !agentStatus.available) return null;

    const remote = agentStatus.remote || {};
    const remoteAllowed = Boolean(remote.remote_marking_allowed);
    const laserConnected = Boolean(agentStatus.connected);
    const laserLabel = agentStatus.profile_display_name || agentStatus.laser_type || "-";
    const jobRunning = phase === "running" || phase === "stopping";
    const busy = phase === "uploading" || phase === "starting";
    const markBusy = busy && pendingAction === "mark";
    const previewBusy = busy && pendingAction === "preview";
    const previewRunning = jobRunning && jobMode === "preview";

    let disabledReason = null;
    if (!remoteAllowed) {
        disabledReason = "Enable “Allow remote marking jobs” in the laser software on this PC.";
    } else if (!laserConnected) {
        disabledReason = "Connect a laser in the laser software on this PC first.";
    }

    const summary = summaryPayload && summaryPayload.summary ? summaryPayload.summary : {};
    // The agent's prediction of the uploaded file, in the pages' wording.
    const agentPrediction = summaryPayload && summaryPayload.predicted_marking_time
        ? summaryPayload.predicted_marking_time
        : null;
    const predictedTime = agentPrediction
        ? (formatMarkingTime(agentPrediction.seconds) || agentPrediction.formatted || null)
        : null;

    let stopLabel = previewRunning ? "Stop preview" : "Stop marking";
    if (phase === "stopping") stopLabel = "Stopping…";

    return (
        <Box sx={{ mt: sectionTitle ? 0 : 1.5, width: "100%" }} data-testid="laser-mark-panel">
            {sectionTitle ? <MarkingSectionLabel>{sectionTitle}</MarkingSectionLabel> : null}
            {children}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                    size="small"
                    color={laserConnected ? "success" : "default"}
                    label={laserConnected ? "Laser connected" : "No laser connected"}
                />
                <Chip
                    size="small"
                    color={remoteAllowed ? "success" : "warning"}
                    label={remoteAllowed ? "Remote marking permitted" : "Remote marking disabled"}
                />
                <MarkingTimeChip value={predictedMarkingTime} />
                {!jobRunning ? (
                    <Button
                        variant="contained"
                        size="small"
                        disableElevation
                        startIcon={markBusy ? <CircularProgress size={14} color="inherit" /> : <PrecisionManufacturingIcon />}
                        disabled={Boolean(disabledReason) || busy}
                        onClick={handleMarkClick}
                    >
                        {markBusy && phase === "uploading" ? "Sending to laser…" : "Mark on laser"}
                    </Button>
                ) : null}
                {!jobRunning ? (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={previewBusy ? <CircularProgress size={14} color="inherit" /> : <VisibilityIcon />}
                        disabled={Boolean(disabledReason) || busy}
                        onClick={handlePreviewClick}
                    >
                        {previewBusy ? "Starting preview…" : "Preview"}
                    </Button>
                ) : null}
                {jobRunning ? (
                    <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<StopIcon />}
                        disabled={phase === "stopping"}
                        onClick={handleStopClick}
                    >
                        {stopLabel}
                    </Button>
                ) : null}
            </Stack>

            {disabledReason && !jobRunning ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {disabledReason}
                </Typography>
            ) : null}

            {previewRunning ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {`Guide laser is tracing the marking outline.${job && job.elapsed_time_formatted ? ` Elapsed ${job.elapsed_time_formatted}.` : ""}`}
                </Typography>
            ) : null}

            {jobRunning && !previewRunning && job ? (
                <Box sx={{ mt: 1, maxWidth: 480 }}>
                    <LinearProgress
                        variant="determinate"
                        value={Math.max(0, Math.min(100, Number(job.progress_percent || 0)))}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        {`${job.progress_percent || 0}% (${job.processed_vectors || 0}/${job.total_vectors || 0} vectors)`}
                        {job.elapsed_time_formatted ? ` — elapsed ${job.elapsed_time_formatted}` : ""}
                    </Typography>
                </Box>
            ) : null}

            {errorMessage ? (
                <Alert severity="error" sx={{ mt: 1, maxWidth: 560 }} onClose={() => setErrorMessage(null)}>
                    {errorMessage}
                </Alert>
            ) : null}
            {resultMessage ? (
                <Alert severity="success" sx={{ mt: 1, maxWidth: 560 }} onClose={() => setResultMessage(null)}>
                    {resultMessage}
                </Alert>
            ) : null}
            {sectionTitle ? <Divider sx={{ my: 2 }} /> : null}

            <Dialog open={phase === "confirm"} onClose={() => setPhaseTracked("idle")} maxWidth="xs" fullWidth>
                <DialogTitle>Start marking on the laser?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        The laser software accepted the marking file. Please confirm before the laser starts.
                    </Typography>
                    <Typography variant="body2">
                        {`Laser: ${laserLabel}`}
                        <br />
                        {`Groups: ${summary.groups ?? "-"} | Vectors: ${summary.vectors_total ?? "-"}`}
                        <br />
                        {predictedTime ? `Predicted marking time: ${predictedTime}` : null}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPhaseTracked("idle")}>Cancel</Button>
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<PrecisionManufacturingIcon />}
                        onClick={handleConfirmStart}
                    >
                        Start marking
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
